/**
 * Fullscreen Canvas Modal Component
 * Phase 4: Fullscreen mode with live preview split view
 *
 * Displays the design canvas and live preview side by side in a fullscreen modal
 */

import React, { useState, useCallback } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useUIStore } from '@atlas-forms/state-react';
import type { FormSchema, FormControl, OperatingMode as FormOperatingMode } from '@atlas-forms/types-js';
import { FormRenderer, FormThemeProvider } from '@atlas-forms/player-components-react';
import { DesignTab } from './tabs/DesignTab';
import '../styles/FullscreenCanvasModal.css';

export interface FullscreenCanvasModalProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;

  /**
   * Callback to close the modal
   */
  onClose: () => void;

  /**
   * Current form schema
   */
  schema: FormSchema;

  /**
   * Selected control ID
   */
  selectedControlId: string | null;

  /**
   * Callback when control is selected
   */
  onSelectControl: (id: string | null) => void;

  /**
   * Callback to add a new control
   */
  onAddControl: (control: Partial<FormControl>) => void;

  /**
   * Callback to delete a control
   */
  onDeleteControl: (id: string) => void;

  /**
   * Callback to duplicate a control
   */
  onDuplicateControl: (id: string) => void;

  /**
   * Callback to reorder controls
   */
  onReorderControls: (startIndex: number, endIndex: number) => void;

  /**
   * Selected tab ID for multi-tab forms
   */
  selectedTabId: string | null;

  /**
   * Callback when tab is selected
   */
  onSelectTab: (tabId: string | null) => void;

  /**
   * Zoom level for the design canvas
   */
  zoomLevel: number;

  /**
   * Whether to show grid in design view
   */
  showGrid: boolean;
}

/**
 * Fullscreen Canvas Modal
 * Shows design and preview side by side
 */
export const FullscreenCanvasModal: React.FC<FullscreenCanvasModalProps> = ({
  isOpen,
  onClose,
  schema,
  selectedControlId,
  onSelectControl,
  onAddControl,
  onDeleteControl,
  onDuplicateControl,
  onReorderControls,
  selectedTabId,
  onSelectTab,
  zoomLevel,
  showGrid,
}) => {
  const { theme } = useUIStore();
  const isDark = theme === 'dark';
  const [previewMode, setPreviewMode] = useState<FormOperatingMode>('edit');
  const [formKey, setFormKey] = useState(0);

  // Handle ESC key to close
  React.useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Handle preview mode change
  const handleModeChange = useCallback((mode: FormOperatingMode) => {
    setPreviewMode(mode);
    setFormKey((k) => k + 1);
  }, []);

  // Backdrop click handler
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  const modeButtonStyle = (active: boolean): React.CSSProperties => ({
    padding: '6px 12px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    transition: 'all 0.15s',
    background: active
      ? isDark ? 'rgba(34,197,94,0.15)' : 'rgba(22,163,74,0.1)'
      : 'transparent',
    color: active
      ? isDark ? '#22c55e' : '#16a34a'
      : isDark ? '#64748b' : '#94a3b8',
    outline: active
      ? `1px solid ${isDark ? 'rgba(34,197,94,0.3)' : 'rgba(34,197,94,0.2)'}`
      : '1px solid transparent',
  });

  return (
    <div
      className="af-fullscreen-modal"
      data-theme={theme}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="fullscreen-modal-title"
    >
      <div className="af-fullscreen-modal__container">
        {/* Header */}
        <div className="af-fullscreen-modal__header">
          <h2 id="fullscreen-modal-title" className="af-fullscreen-modal__title">
            Fullscreen Editor
          </h2>
          <button
            className="af-fullscreen-modal__close"
            onClick={onClose}
            aria-label="Close fullscreen mode"
            title="Close (ESC)"
          >
            ✕
          </button>
        </div>

        {/* Content - Split View */}
        <div className="af-fullscreen-modal__content">
          <PanelGroup direction="horizontal">
            {/* Left Panel - Design View */}
            <Panel defaultSize={50} minSize={30}>
              <div className="af-fullscreen-modal__panel">
                <div className="af-fullscreen-modal__panel-header">
                  <h3 className="af-fullscreen-modal__panel-title">Design View</h3>
                  <div className="af-fullscreen-modal__panel-info">
                    {schema.controls.length} {schema.controls.length === 1 ? 'control' : 'controls'}
                  </div>
                </div>
                <div className="af-fullscreen-modal__panel-content">
                  <DesignTab
                    schema={schema}
                    selectedControlId={selectedControlId}
                    onSelectControl={onSelectControl}
                    onAddControl={onAddControl}
                    onDeleteControl={onDeleteControl}
                    onDuplicateControl={onDuplicateControl}
                    onReorderControls={onReorderControls as any}
                    zoomLevel={zoomLevel}
                    showGrid={showGrid}
                    selectedTabId={selectedTabId}
                    onSelectTab={onSelectTab}
                    compact
                  />
                </div>
              </div>
            </Panel>

            <PanelResizeHandle className="af-fullscreen-modal__resize-handle" />

            {/* Right Panel - Preview */}
            <Panel defaultSize={50} minSize={30}>
              <div className="af-fullscreen-modal__panel">
                <div className="af-fullscreen-modal__panel-header">
                  <h3 className="af-fullscreen-modal__panel-title">Live Preview</h3>
                  <div className="af-fullscreen-modal__panel-controls">
                    <span className="af-fullscreen-modal__panel-label">Mode:</span>
                    <button
                      type="button"
                      style={modeButtonStyle(previewMode === 'design')}
                      onClick={() => handleModeChange('design')}
                    >
                      Design
                    </button>
                    <button
                      type="button"
                      style={modeButtonStyle(previewMode === 'edit')}
                      onClick={() => handleModeChange('edit')}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      style={modeButtonStyle(previewMode === 'view')}
                      onClick={() => handleModeChange('view')}
                    >
                      View
                    </button>
                  </div>
                </div>
                <div className="af-fullscreen-modal__panel-content af-fullscreen-modal__panel-content--preview">
                  <FormThemeProvider
                    theme={{
                      mode: theme,
                      primaryColor: '#16a34a',
                      borderRadius: 6,
                    } as any}
                  >
                    <FormRenderer
                      key={formKey}
                      schema={schema}
                      mode={previewMode as any}
                      onSubmit={async () => {}}
                    />
                  </FormThemeProvider>
                </div>
              </div>
            </Panel>
          </PanelGroup>
        </div>

        {/* Footer with keyboard shortcuts */}
        <div className="af-fullscreen-modal__footer">
          <span className="af-fullscreen-modal__hint">
            <kbd>ESC</kbd> to close • Drag the divider to resize panels
          </span>
        </div>
      </div>
    </div>
  );
};

export default FullscreenCanvasModal;
